import React, { useState, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton, type BaseOwnProps as AtlaskitButtonProps } from '@atlaskit/button';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ConfluenceSpace } from '../../common/types.tsx';
import { useNavigateToEditor } from '../../controllers/hooks/use-navigate-to-editor/index.tsx';
import { useIsFetchingDraftDetails } from '../../controllers/store/index.tsx';
import messages from './messages.tsx';
import { CreateArticleModal } from './modal/index.tsx';

type CreateArticleProps = {
	hasConfluenceLicense: boolean;
	spaces: ConfluenceSpace[];
	appearance?: AtlaskitButtonProps['appearance'];
	children?: string;
};

export const CreateArticleButton = ({
	spaces,
	appearance = 'primary',
	children,
	hasConfluenceLicense,
}: CreateArticleProps) => {
	const intl = useIntl();
	const [showModal, toggleModal] = useState<boolean>(false);
	const [isFetchingDraftDetails] = useIsFetchingDraftDetails();
	const { navigateToEditor } = useNavigateToEditor({ spaces });

	const showCreateArticleModal = (e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireTrackAnalytics(analyticsEvent, 'createArticleBtn clicked', {
			hasConfluenceLicense: Boolean(hasConfluenceLicense),
		});
		if (spaces.length === 1) {
			navigateToEditor();
		} else {
			toggleModal(true);
		}
	};

	const hideCreateArticleModal = () => {
		toggleModal(false);
	};
	const isSSR = __SERVER__;
	const text = children !== undefined ? children : intl.formatMessage(messages.text);

	return !isSSR && spaces.length > 0 ? (
		<>
			{spaces.length === 1 ? (
				<LoadingButton
					appearance={appearance}
					onClick={showCreateArticleModal}
					isLoading={isFetchingDraftDetails}
				>
					{text}
				</LoadingButton>
			) : (
				<Button appearance={appearance} onClick={showCreateArticleModal}>
					{text}
				</Button>
			)}
			{showModal && <CreateArticleModal onCancel={hideCreateArticleModal} />}
		</>
	) : null;
};
